import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Cancel, Save } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import {
  useCreateInventoryLinkHook,
  useUpdateInventoryLinkHook,
} from "../../../hooks/useLessonPlanHook";
import { logger } from "../../../utils/common";
import { TLessonPlanInventoryLink } from "../../../apis/lessonPlan/lessonPlan.interfaces";

const createSchema = z.object({
  inventoryId: z.string().uuid("Inventory ID must be a valid UUID").or(z.string().min(1)),
  quantityRequired: z
    .union([
      z.coerce.number().int("Quantity must be a whole number").min(0),
      z.literal(""),
    ])
    .optional(),
  notes: z.string().max(2000).optional().or(z.literal("")),
});

const editSchema = z.object({
  inventoryId: z.string(),
  quantityRequired: z
    .union([
      z.coerce.number().int("Quantity must be a whole number").min(0),
      z.literal(""),
    ])
    .optional(),
  notes: z.string().max(2000).optional().or(z.literal("")),
});

type LinkFormData = z.infer<typeof createSchema>;

interface ManageInventoryLinkDialogProps {
  open: boolean;
  onClose: () => void;
  lessonPlanId: string;
  link?: TLessonPlanInventoryLink | null;
}

const buildDefaults = (
  link?: TLessonPlanInventoryLink | null,
): LinkFormData => ({
  inventoryId: link?.inventoryId ?? "",
  quantityRequired: link?.quantityRequired ?? "",
  notes: link?.notes ?? "",
});

const ManageInventoryLinkDialog: FC<ManageInventoryLinkDialogProps> = ({
  open,
  onClose,
  lessonPlanId,
  link,
}) => {
  const isEditMode = !!link;
  const createMutation = useCreateInventoryLinkHook();
  const updateMutation = useUpdateInventoryLinkHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LinkFormData>({
    resolver: zodResolver(isEditMode ? editSchema : createSchema),
    defaultValues: buildDefaults(link),
  });

  useEffect(() => {
    if (open) reset(buildDefaults(link));
  }, [open, link, reset]);

  const onSubmit = async (data: LinkFormData) => {
    const quantity =
      typeof data.quantityRequired === "number"
        ? data.quantityRequired
        : undefined;

    try {
      if (isEditMode && link) {
        await updateMutation.mutateAsync({
          lessonPlanId,
          inventoryLinkId: link.id,
          request: {
            quantityRequired: quantity,
            notes: data.notes || undefined,
          },
        });
        logger.show_success("Inventory link updated.");
      } else {
        await createMutation.mutateAsync({
          lessonPlanId,
          request: {
            inventoryId: data.inventoryId,
            quantityRequired: quantity,
            notes: data.notes || undefined,
          },
        });
        logger.show_success("Inventory link created.");
      }
      onClose();
    } catch (err) {
      logger.show_error(
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? "update" : "create"} inventory link.`,
      );
    }
  };

  const activeMutation = isEditMode ? updateMutation : createMutation;
  const isLoading = isSubmitting || activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorMessage = activeMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="manage-inventory-link-dialog"
    >
      <DialogTitle>
        {isEditMode ? "Edit Inventory Link" : "Link Inventory Item"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "Failed to save inventory link."}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <ControlledTextField
              control={control}
              name="inventoryId"
              id="inventory-id"
              label="Inventory ID"
              required
              disabled={isEditMode}
              errorMessage={errors.inventoryId?.message}
              helperText={
                isEditMode
                  ? "Inventory item is immutable; delete and re-create to swap."
                  : "Inventory UUID. Must belong to the same institute."
              }
            />
            <ControlledTextField
              control={control}
              name="quantityRequired"
              id="inventory-quantity"
              label="Quantity Required"
              type="number"
              errorMessage={errors.quantityRequired?.message}
              inputProps={{ min: 0 }}
            />
            <ControlledTextField
              control={control}
              name="notes"
              id="inventory-notes"
              label="Notes"
              multiline
              rows={2}
              errorMessage={errors.notes?.message}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
          >
            {isLoading
              ? isEditMode
                ? "Saving..."
                : "Linking..."
              : isEditMode
                ? "Save Changes"
                : "Link Item"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageInventoryLinkDialog;
